import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Rx'
import jwtDecode from 'jwt-decode'
import {
  CreateDeliveryPayload,
  LoginPayload,
  customDeliveryCreation,
  customEstimate,
  customPageLogin,
  getCustomSettingsBySlug,
} from 'store/services/customPages'
import { CustomPage } from 'interfaces/custompage'
import { parseDeliveryPayload } from 'store/utils/delivery'
import * as deliveryDuck from 'store/ducks/delivery'
import { creationFailure, creationSuccess, estimationFailure, estimationSuccess } from './delivery'
import { alertErrorDefault } from './alert'
import { GlobalState } from './state'
import { LOCALSTORAGE_REFRESH_TOKEN, REFRESH_TOKEN_FAILURE, REFRESH_TOKEN_SUCCESS } from './authentication'

// Actions
export const CUSTOM_PAGE_LOGIN_REQUEST = 'customPage/CUSTOM_PAGE_LOGIN_REQUEST'
export const CUSTOM_PAGE_LOGIN_SUCCESS = 'customPage/CUSTOM_PAGE_LOGIN_SUCCESS'
export const CUSTOM_PAGE_LOGIN_FAILURE = 'customPage/CUSTOM_PAGE_LOGIN_FAILURE'
export const FETCH_CUSTOM_SETTINGS = 'customPage/FETCH_CUSTOM_SETTINGS'
export const FETCH_CUSTOM_SETTINGS_SUCCESS = 'customPage/FETCH_CUSTOM_SETTINGS_SUCCESS'
export const CUSTOMPAGE_ESTIMATION_REQUEST = 'customPage/CUSTOMPAGE_ESTIMATION_REQUEST'
export const CUSTOMPAGE_CREATION_DELIVERY_REQUEST = 'customPage/CUSTOMPAGE_CREATION_DELIVERY_REQUEST'

// Reducer

export type CustomPageState = {
  token: string
  authenticated: boolean
  loading: boolean
  errors: string[]
  customSettings: CustomPage
}

export type CustomPageAction = Record<string, unknown>
const INITIAL_STATE = {
  token: null,
  authenticated: false,
  loading: false,
  errors: [],
  customSettings: null,
}

export function reducer(state: CustomPageState = INITIAL_STATE, action: CustomPageAction) {
  switch (action.type) {
    case CUSTOM_PAGE_LOGIN_REQUEST:
      return {
        ...state,
        token: null,
        authenticated: false,
        loading: true,
        errors: [],
      }

    case CUSTOM_PAGE_LOGIN_SUCCESS:
      return {
        ...state,
        token: action.token,
        authenticated: true,
        loading: false,
        errors: [],
      }

    case CUSTOM_PAGE_LOGIN_FAILURE:
      return {
        ...state,
        errors: action.errors,
        loading: false,
      }

    case FETCH_CUSTOM_SETTINGS:
      return {
        ...state,
        loading: true,
      }

    case FETCH_CUSTOM_SETTINGS_SUCCESS:
      return { ...state, customSettings: action.payload, loading: false }

    case REFRESH_TOKEN_FAILURE:
      return {
        ...INITIAL_STATE,
      }
    case REFRESH_TOKEN_SUCCESS:
      return {
        ...state,
        token: action.token,
        authenticated: true,
        loading: false,
        errors: [],
      }
    default:
      return state
  }
}

// Actions Creators
export const customPageLoginRequest = (payload: LoginPayload) => ({
  type: CUSTOM_PAGE_LOGIN_REQUEST,
  payload,
})

export const customPageloginSuccess = (token, payload) => ({
  type: CUSTOM_PAGE_LOGIN_SUCCESS,
  token,
  payload,
})

export const customPageloginFailure = (errors) => {
  return {
    type: CUSTOM_PAGE_LOGIN_FAILURE,
    errors,
  }
}

export const fetchCustomSettings = (payload) => {
  return {
    type: FETCH_CUSTOM_SETTINGS,
    payload,
  }
}

export const fetchCustomSettingsSuccess = (payload) => {
  return {
    type: FETCH_CUSTOM_SETTINGS_SUCCESS,
    payload,
  }
}

export const customEstimationRequest = (payload: CreateDeliveryPayload, slug: string) => {
  return {
    type: CUSTOMPAGE_ESTIMATION_REQUEST,
    payload: parseDeliveryPayload(payload),
    slug,
  }
}

export const customDeliveryCreationRequest = (payload, slug: string) => ({
  type: CUSTOMPAGE_CREATION_DELIVERY_REQUEST,
  payload: parseDeliveryPayload(payload),
  slug,
})

export const customPagesRefreshTokenSuccess = (token, payload, retried = false) => ({
  type: REFRESH_TOKEN_SUCCESS,
  token,
  payload,
  retried,
})

export const customPagesRefreshTokenFailure = () => ({
  type: REFRESH_TOKEN_FAILURE,
})

export const epic = combineEpics(
  customLoginRequestEpic,
  fetchCustomSettingsEpic,
  customEstimationRequestEpic,
  customDeliveryCreationRequestEpic,
)

export function customLoginRequestEpic(action$, store) {
  return action$.ofType(CUSTOM_PAGE_LOGIN_REQUEST).mergeMap((action) =>
    Observable.ajax(customPageLogin(action.payload))
      .do((res) => localStorage.setItem(LOCALSTORAGE_REFRESH_TOKEN, res.response.refresh_token))
      .map((res) => customPageloginSuccess(res.response.token, jwtDecode(res.response.token)))
      .catch(() => {
        const errors = [store.getState().intl.messages['ducks.authentication.login.error']]
        return Observable.of(customPageloginFailure(errors), alertErrorDefault() as unknown)
      }),
  )
}

export function fetchCustomSettingsEpic(action$, store) {
  return action$
    .ofType(FETCH_CUSTOM_SETTINGS)
    .mergeMap((action) =>
      Observable.ajax(getCustomSettingsBySlug(action.payload)).map((res) => fetchCustomSettingsSuccess(res.response)),
    )
}

export function customEstimationRequestEpic(action$) {
  return action$.ofType(CUSTOMPAGE_ESTIMATION_REQUEST).mergeMap((action) => {
    return Observable.ajax(customEstimate({ payload: action.payload, slug: action.slug }))
      .mergeMap((res) => {
        return Observable.of(deliveryDuck.estimationReset(), estimationSuccess(res.response))
      })
      .catch((err) => {
        try {
          const errors = [...(err.response.violations || []), ...(err.response.errors || [])].map((e) =>
            typeof e === 'string' ? e : e.message,
          )

          return Observable.of(estimationFailure(errors), alertErrorDefault() as unknown)
        } catch (e) {
          return Observable.of(estimationFailure(), alertErrorDefault() as unknown)
        }
      })
  })
}

export function customDeliveryCreationRequestEpic(action$) {
  return action$.ofType(CUSTOMPAGE_CREATION_DELIVERY_REQUEST).mergeMap((action) => {
    return Observable.ajax(customDeliveryCreation({ payload: action.payload, slug: action.slug }))
      .map((res) => {
        return creationSuccess(res.response)
      })
      .catch((err) => {
        try {
          const errors = [...(err.response.violations || []), ...(err.response.errors || [])].map((e) =>
            typeof e === 'string' ? e : e.message,
          )

          return Observable.of(creationFailure(errors), alertErrorDefault() as unknown)
        } catch (e) {
          return Observable.of(creationFailure(), alertErrorDefault() as unknown)
        }
      })
  })
}

export const selectCustomAuthenticated = (state: GlobalState) => state.customPage.authenticated
